<script setup lang="ts">
import { computed, nextTick, onBeforeUnmount, onMounted, ref, watch } from 'vue'
import { usePageContext } from '@/composables/usePageContext'
import { useScreen } from '@/composables/useScreen'
import { debounce } from 'lodash-es'
import { clearEmpty, mapObjectToOption } from '@/helpers/dataMappers'
import { apiPost } from '@/lib/http'
import type { SelectOption } from '@/types/api'
import AppNavigation from '@/ui-kit/layout/AppNavigation.vue'
import SeminarCard from '@/ui-kit/seminars/SeminarCard.vue'
import SeminarCardSlider from '@/ui-kit/seminars/SeminarCardSlider.vue'
import RequiresAuthModal from '@/plugins/RequiresAuth/RequiresAuthModal.vue'
import Swiper from 'swiper'
import { Autoplay, Navigation } from 'swiper/modules'
import 'swiper/css'
import 'swiper/css/navigation'
import type { PaymentModalExpose } from '~/ui-kit/components/PaymentModal/PaymentModal.vue'

interface ShowcaseButton {
  buttonType?: string
  buttonText: string
  buttonLink: string
}

interface ShowcaseCard {
  id: number
  title: string
  description: string
  type: string
  dateStart: string | null
  status: string
  staff: Array<{ group: string; link?: string; teachers: Array<{ name: string }> }>
  duration: number
  lockReason: string
  buttons: ShowcaseButton[]
  cardLink: string
}

interface ShowcaseAuthor {
  topic: string
  name: string
  description: string
  avatar: string
}

interface ShowcaseBlock {
  author?: ShowcaseAuthor
  cards: ShowcaseCard[]
  slider?: boolean
}

interface ShowcaseCategory {
  title: string
  icon: string
  blocks: ShowcaseBlock[]
}

interface ShowcaseContext {
  languages: Array<{ id: number; name: string }>
  urls: {
    all_items: string
    now_available_items: string
    coming_events_items: string
    my_education: string
    purchase_materials: string
    purchase_teachyourselfbook: string
  }
}

declare global {
  interface Window {
    purchase_materials?: (id: number, price: number, title: string) => void
    purchase_teachyourselfbook?: (id: number, price: number, title: string) => void
  }
}

const ctx = usePageContext<ShowcaseContext>()
const { isMobile } = useScreen()

const paymentModal = ref<PaymentModalExpose | null>(null)
const comingEventsRef = ref<HTMLElement | null>(null)
const comingEventsPrevArrow = ref<HTMLElement | null>(null)
const comingEventsNextArrow = ref<HTMLElement | null>(null)

const isFiltersVisible = ref(false)
const isAvailableSeminarsVisible = ref(true)
const isAvailable = ref(false)
const language = ref<number | null>(ctx.languages[0]?.id ?? null)
const level = ref('all')

const productsBlocks = ref<ShowcaseCategory[]>([])
const nowAvailableList = ref<ShowcaseCard[]>([])
const comingEventsList = ref<ShowcaseCard[]>([])
const swiper = ref<Swiper | null>(null)

const languages = computed<SelectOption[]>(() => ctx.languages.map(mapObjectToOption))

const levelsList: SelectOption[] = [
  { value: 'all', text: 'Все' },
  { value: 'seminar', text: 'Семинары' },
  { value: 'intensive', text: 'Интенсивы' },
  { value: 'ace_course', text: 'Курсы АС' },
  { value: 'training', text: 'Тренинги' },
  { value: 'additional', text: 'Дополнительное обучение' }
]

const levelNumber = computed({
  get() {
    return levelsList.findIndex(item => item.value === level.value)
  },
  set(value: number) {
    level.value = String(levelsList[value]?.value ?? 'all')
  }
})

const languageNumber = computed({
  get() {
    return languages.value.findIndex(item => item.value === language.value)
  },
  set(value: number) {
    const selected = languages.value[value]?.value
    language.value = typeof selected === 'number' ? selected : null
  }
})

const labelOptions = computed(() => {
  return isMobile.value
    ? { mode: 'bold' }
    : {
        mode: 'bold horizontal',
        labelWidth: 84
      }
})

const filtersCount = computed(() => {
  return [language.value, level.value, isAvailable.value].reduce<number>((acc, value) => {
    return acc + Number(Boolean(value))
  }, 0)
})

const hasProducts = computed(() => {
  return productsBlocks.value.filter(category => {
    return category.blocks.some(block => block.cards.length > 0)
  })
})

const refreshDebounced = debounce(() => {
  refresh()
}, 300)

watch([level, language, isAvailable], refreshDebounced)

watch(isMobile, () => {
  initGallery()
})

async function refresh() {
  const payload = clearEmpty({
    level: level.value,
    language: language.value,
    available: isAvailable.value
  })

  const result = await apiPost<ShowcaseCategory[]>(ctx.urls.all_items, payload)
  productsBlocks.value = result.data ?? []
}

async function refreshNowAvailable() {
  const result = await apiPost<ShowcaseCard[]>(ctx.urls.now_available_items)
  nowAvailableList.value = result.data ?? []
}

async function refreshComingEvents() {
  const result = await apiPost<ShowcaseCard[]>(ctx.urls.coming_events_items)
  comingEventsList.value = result.data ?? []
  await initGallery()
}

function toggleComingEventsVisibility() {
  isAvailableSeminarsVisible.value = !isAvailableSeminarsVisible.value
  localStorage.setItem('__isAvailableSeminarsVisible', String(isAvailableSeminarsVisible.value))
}

async function initGallery() {
  swiper.value?.destroy(true, true)
  swiper.value = null

  if (!comingEventsList.value.length) return

  await nextTick()

  if (!comingEventsRef.value || !comingEventsPrevArrow.value || !comingEventsNextArrow.value) {
    return
  }

  swiper.value = new Swiper(comingEventsRef.value, {
    modules: [Navigation, Autoplay],
    spaceBetween: 16,
    slidesPerView: isMobile.value ? 'auto' : 2,
    navigation: {
      nextEl: comingEventsNextArrow.value,
      prevEl: comingEventsPrevArrow.value
    },
    autoplay: {
      delay: 5000
    }
  })
}

function buyMaterials(seminar: { id: number; price: number; title: string }) {
  paymentModal.value?.open({
    url: ctx.urls.purchase_materials,
    order: {
      name: `Лекции семинара ${seminar.title}`,
      price: seminar.price
    },
    query: {
      seminar_id: seminar.id
    }
  })
}

function buyTeachYourselfBook(book: { id: number; price: number; title: string }) {
  paymentModal.value?.open({
    enableCredit: true,
    url: ctx.urls.purchase_teachyourselfbook,
    order: {
      name: `Самоучитель ${book.title}`,
      price: book.price
    },
    query: {
      book_id: book.id
    }
  })
}

onMounted(() => {
  window.purchase_materials = (id, price, title) => buyMaterials({ id, price, title })
  window.purchase_teachyourselfbook = (id, price, title) => buyTeachYourselfBook({ id, price, title })

  refresh()
  refreshNowAvailable()
  refreshComingEvents()

  const localValue = localStorage.getItem('__isAvailableSeminarsVisible')
  if (localValue) {
    isAvailableSeminarsVisible.value = localValue === 'true'
  }
})

onBeforeUnmount(() => {
  delete window.purchase_materials
  delete window.purchase_teachyourselfbook
  refreshDebounced.cancel()
  swiper.value?.destroy(true, true)
  swiper.value = null
})
</script>

<template>
  <div class="account-frame showcase-page">
    <AppNavigation role="teacher" rules-url="/website/documents/oferta" />

    <div class="content">
      <v-notifications position="bottom right" />
      <RequiresAuthModal />
      <v-payment-modal ref="paymentModal" :url="ctx.urls.purchase_materials" />

      <header class="header">
        <h1 class="header__title">Семинары и курсы</h1>
      </header>

      <v-buttons-group mode="inactive" :model-value="1" class="mb-2">
        <v-button mode="primary filled block large" :href="ctx.urls.my_education">Мое обучение</v-button>
        <v-button mode="primary filled block large">Витрина</v-button>
      </v-buttons-group>

      <main>
        <div v-if="isMobile" class="sort d-flex mt-1 mb-3">
          <v-button mode="large block flat" @click="isFiltersVisible = true">
            Фильтры
            <span class="blue-text ml-1">{{ filtersCount }}</span>
          </v-button>
        </div>

        <div class="filters d-flex" :class="{ _visible: isFiltersVisible }">
          <div v-if="isMobile" class="filters__header">
            <h3 class="text-h3 mr-auto">Фильтры</h3>
            <v-button mode="text" @click="isFiltersVisible = false">
              <v-icon>mdi-close</v-icon>
            </v-button>
          </div>

          <div class="filters__container">
            <template v-if="isMobile">
              <v-field class="filters__language mt-3 mr-d-2" v-bind="labelOptions" label="Язык">
                <v-select v-model="language" class="filters__language-select" :items="languages" />
              </v-field>

              <v-field class="filters__level mt-3 mr-d-2" v-bind="labelOptions" label="Статус">
                <v-radio-group v-model="level" :items="levelsList" />
              </v-field>

              <v-field class="filters__level mt-3 mr-d-2" v-bind="labelOptions" label="Доступность">
                <v-checkbox v-model="isAvailable">Только доступные мне</v-checkbox>
              </v-field>

              <v-button class="mt-auto" mode="primary large block" @click="isFiltersVisible = false">
                Применить фильтры
              </v-button>
            </template>

            <template v-else>
              <v-field class="filters__language" v-bind="labelOptions" label="Язык">
                <v-buttons-group v-model="languageNumber">
                  <v-button v-for="lang in languages" :key="lang.value" mode="primary-low small">
                    {{ lang.text }}
                  </v-button>
                </v-buttons-group>
              </v-field>

              <v-field class="filters__level mt-2" v-bind="labelOptions" label="Статус">
                <v-buttons-group v-model="levelNumber">
                  <v-button v-for="item in levelsList" :key="item.value" mode="primary-low small">
                    {{ item.text }}
                  </v-button>
                </v-buttons-group>
              </v-field>

              <v-field class="filters__available">
                <v-checkbox v-model="isAvailable">Только доступные мне</v-checkbox>
              </v-field>
            </template>
          </div>
        </div>

        <div class="products mt-6">
          <div class="products-block">
            <h3 class="products-block__title">
              Стало доступно
              <v-link class="products-block__hide" mode="primary" @click="toggleComingEventsVisibility">
                {{ isAvailableSeminarsVisible ? 'Скрыть' : 'Показать' }}
              </v-link>
            </h3>

            <div v-if="isAvailableSeminarsVisible" class="seminars-unite">
              <SeminarCard
                v-for="seminar in nowAvailableList"
                :key="seminar.id"
                v-bind="seminar"
                class="seminars-unite__seminar"
                mode="wide"
              />
            </div>
          </div>
        </div>

        <div class="products mt-6">
          <div class="products-block">
            <h3 class="products-block__title">
              Мои ближайшие события

              <div class="products-block__controls d-flex ml-auto">
                <button ref="comingEventsPrevArrow" class="products-block__control _left">
                  <v-icon size="32">mdi-chevron-left</v-icon>
                </button>
                <button ref="comingEventsNextArrow" class="products-block__control _right">
                  <v-icon size="32">mdi-chevron-right</v-icon>
                </button>
              </div>
            </h3>

            <div v-if="comingEventsList.length > 0" ref="comingEventsRef" class="seminars-carousel swiper">
              <div class="swiper-wrapper">
                <SeminarCard
                  v-for="seminar in comingEventsList"
                  :key="seminar.id"
                  v-bind="seminar"
                  class="seminars-carousel__seminar swiper-slide"
                  mode="large"
                />
              </div>
            </div>
          </div>
        </div>

        <template v-for="category in hasProducts" :key="category.title">
          <div class="products mt-6">
            <h3 class="products-block__title">
              <v-icon class="mr-1" size="32">{{ category.icon }}</v-icon>
              {{ category.title }}
            </h3>

            <div v-for="(block, idx) in category.blocks" :key="idx" class="products-block">
              <div v-if="block.author" class="author">
                <div class="author__info">
                  <span class="author__topic">{{ block.author.topic }}</span>
                  <h3 class="author__name">{{ block.author.name }}</h3>
                </div>
                <p class="author__description">{{ block.author.description }}</p>
                <v-img class="author__avatar" :src="block.author.avatar" :width="120" :height="120" />
              </div>

              <SeminarCardSlider v-if="block.slider && block.cards.length" :seminars="block.cards" />

              <div v-else class="seminars">
                <SeminarCard
                  v-for="seminar in block.cards"
                  :key="seminar.id"
                  v-bind="seminar"
                  class="seminars__seminar"
                  :mode="seminar.id === 0 ? 'large' : ''"
                />
              </div>
            </div>
          </div>
        </template>

        <v-footer />
      </main>
    </div>
  </div>
</template>

<style lang="scss">
@use '@/styles/pages/seminars/showcase.scss' as *;
</style>
